<template>
  <div
    class="product-card"
    :class="[
      $blockClass(blockClass, 'product-card'),
      {
        [$class('loading')]: state === 'loading',
      },
    ]"

    :data-product-page="localProduct.page"
    @mouseenter="loadSecondaryImage = true"
  >
    <div class="product-card__featured-image">
      <a
        class="product-card__link"
        :href="localProduct.url"
        :aria-label="localProduct.title"
      >
        <responsive-image
          :key="`featured-image-${localProduct.handle}`"
          class="product-card__responsive-image"
          :class="$blockClass(blockClass, 'product-card-image')"
          block-class="product-card"
          :image="state === 'ready' ? localProduct.featuredImage : {}"
          min-max="200-650"
          ratio="4:5"
          :style="{ animationDelay: animationDelay }"
        />

        <responsive-image
          v-if="localProduct?.images?.length > 1 && loadSecondaryImage"
          :key="`second-image-${localProduct.handle}`"
          class="product-card__responsive-hover-image"
          :class="$blockClass(blockClass, 'product-card-image')"
          block-class="product-card"
          :image="state === 'ready' ? localProduct.images[1] : {}"
          min-max="200-650"
          ratio="4:5"
          :padding="false"
        />
      </a>

      <div
        v-if="localProduct.badge"
        :key="`badge-${localProduct.handle}`"
        class="product-card__badge text-utility-label-xs text-utility-label-s-desktop"
        v-text="localProduct.badge"
      />
    </div>

    <div
      class="product-card__footer"
      :class="$blockClass(blockClass, 'product-card-footer')"
    >
      <a
        class="product-card__footer--content"
        :href="localProduct.url"
        :aria-label="localProduct.title"
      >
        <p
          v-if="productCollection || state === 'loading'"
          class="product-card__vendor text-utility-label-xs"
          :class="[
            $blockClass(blockClass, 'product-card-vendor'),
            { 'text-utility-label-s-desktop': !isSearchInstant }
          ]"
          :style="{ animationDelay: animationDelay }"
          v-text="productCollection"
        />

        <p
          class="product-card__title text-utility-capitalized-s"
          :class="$blockClass(blockClass, 'product-card-title')"
          :style="{ animationDelay: animationDelay }"
          v-text="localProduct.title"
        />

        <p
          v-if="isOneOfAKind"
          class="text-utility-capitalized-s"
          v-text="$string('product.form.poa_text')"
        />

        <div
          v-else
          class="product-card__guide"
        >
          <p
            v-if="isGuidePrice"
            class="text-utility-capitalized-s"
            v-text="$string('product.form.guide_text')"
          />

          <product-prices
            :class="$blockClass(blockClass, 'product-card-prices')"
            class-normal="text-utility-capitalized-s"
            block-class="product-card"
            :compare-at-price="localProduct?.compareAtPrice?.amount"
            :price="localProduct?.price?.amount"
            :style="{ animationDelay: animationDelay }"
            :varies="localProduct?.price?.varies"
          />
        </div>

        <p
          v-if="textColor && localColours"
          class="text-utility-label-xs-desktop text-utility-label-s product-card__text-color mobile"
          v-text="$string('product.swatches.length', {
            pluralise: Object.keys(localColours).length,
            replace: {
              count: Object.keys(localColours).length,
            }
          })"
        />
      </a>

      <product-wishlist
        v-if="showWishlistButton"
        class="product-card__wishlist"
        :product="product"
      />
    </div>

    <p
      v-if="textColor && localColours"
      class="text-utility-label-xs-desktop text-utility-label-s product-card__text-color desktop"
      v-text="$string('product.swatches.length', {
        pluralise: Object.keys(localColours).length,
        replace: {
          count: Object.keys(localColours).length,
        }
      })"
    />

    <colour-swatches
      v-else-if="localColours"
      class="product-card__swatches"
      :animation-delay="animationDelay"
      :colours="localColours"
      :handle="localProduct.handle"
      limited
      :state="state"
      :visible="visible"
      @update-handle="handleUpdateHandle"
    />
  </div>
</template>

<script>

/**
 * Vue: Product card
 * -----------------------------------------------------------------------------
 * Product card used throughout the site.
 *
 * @param {String} [blockClass] - Block level class to apply to all elements.
 * @param {Number} [index] - Product card's forloop index.
 * @param {Boolean|Object} product - Product object, false shows loading state.
 * @param {Boolean} [visible] - Product card is visible in carousel.
 *
 */
import { nextTick } from 'vue'

import ColourSwatches from '~async/colour-swatches/colour-swatches'
import ProductPrices from '~global/product-prices/product-prices'
import ProductWishlist from '~async/product-wishlist/product-wishlist'
import ResponsiveImage from '~global/images/responsive-image'

import query from '~/graphql/queries/product.gql'
import { formatProduct } from '~/helpers/format'

export default {
  name: 'ProductCard',

  components: {
    ColourSwatches,
    ProductPrices,
    ProductWishlist,
    ResponsiveImage,
  },

  props: {
    blockClass: {
      type: String,
      default: '',
    },
    index: {
      type: Number,
      default: 0,
    },
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    visible: {
      type: Boolean,
      default: true,
    },
    showWishlistButton: {
      type: Boolean,
      default: true,
    },
    textColor: {
      type: Boolean,
      default: false,
    },
    isSearchInstant: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      loadSecondaryImage: false,
      localColours: false,
      localProduct: false,
      state: 'ready',
    }
  },

  computed: {

    /**
     * Compute animation delay.
     * @returns {String}
     */
    animationDelay() {
      return this.$timing('quick', { index: this.index, unit: 's' })
    },

    /**
     * Product collection.
     * @returns {String|Boolean}
     */
    productCollection() {
      const collectionTag = 'Collection:'

      if (!this.localProduct || !this.localProduct.tags.some(tag => tag.includes(collectionTag))) {
        return false
      }

      return this.localProduct.tags.find(tag => tag.includes(collectionTag))
        .replace(collectionTag, '').trim()
    },

    /**
     * One of a kind product.
     * @returns {Boolean}
     */
    isOneOfAKind() {
      return this.localProduct?.tags?.some(tag => tag.includes('type: one_of_a_kind'))
    },

    /**
     * If product has guide price.
     * @returns {Boolean}
     */
    isGuidePrice() {
      return this.localProduct?.tags?.some(tag => tag.includes('type: guide_price'))
    },
  },

  watch: {

    /**
     * Watch product updates to update local product.
     * - Update state when false.
     */
    product: {
      handler() {
        nextTick().then(() => {
          this.setLocalProduct()
          this.state = this.localProduct === false ? 'loading' : 'ready'
        })
      },
      immediate: true,
    },
  },

  mounted() {
    if (!cnvs.storybook || this.localProduct) {
      return
    }

    if (!this.product) {
      this.state = 'loading'
      return
    }

    this.setLocalProduct()
  },

  methods: {

    /**
     * Set local product.
     */
    setLocalProduct() {
      if (this.localProduct.handle === this.product.handle) {
        return
      }

      this.localProduct = this.product
      this.localColours = this.product.colours
      this.setStorage(this.localProduct)
    },

    /**
     * Update display product based on handle emit.
     * @param {String} handle - New handle to load.
     */
    handleUpdateHandle(handle) {
      if (handle === false || handle === this.localProduct.handle) {
        return
      }

      if (
        !cnvs.settings.disableProductSaving &&
        sessionStorage.getItem(`cnvs-product-${handle}`)
      ) {
        this.localProduct = JSON.parse(sessionStorage.getItem(`cnvs-product-${handle}`))
        return
      }

      this.queryProduct(handle)
    },

    /**
     * Query product.
     * @param {String} handle - Handle to load.
     */
    async queryProduct(handle) {
      this.state = 'loading'

      try {
        const response = await cnvs.Query({
          query,
          variables: {
            country: cnvs.store.country,
            handle,
            language: cnvs.store.language,
          },
        })

        if (cnvs.settings.disableComponentReadyState) {
          return
        }

        this.localProduct = formatProduct(response)
        this.setStorage(this.localProduct)
        this.state = 'ready'

      } catch (error) {
        reportError('Failed to load product', error)
      }
    },

    /**
     * Set product to sessionStorage.
     * @param {Object} product - Product to update.
     */
    setStorage(product) {
      if (cnvs.settings.disableProductSaving) {
        return
      }

      sessionStorage.setItem(
        `cnvs-product-${product.handle}`,
        JSON.stringify(product),
      )
    },
  },
}
</script>

<style lang="scss">
@import './product-card';
</style>
